import {useCallback, useMemo} from "react";
import {Trans, useTranslation} from "react-i18next";
import {useRouteMatch} from "react-router-dom";

import {IMonthCityStats} from "../../../../../api/src/db_queries/month_city_stats_query";
import {IMonthCityTypeStats} from "../../../../../api/src/db_queries/month_city_type_stats_query";
import {getCityMapRegionParams, getCityOption} from "../../../../../config/cities";
import {mapCitySlugToRegionType} from "../../../../../config/regions";
import {originDate} from "../../../../../utils/dates";
import {useAppDispatch, useAppSelector} from "../../../../../utils/hooks/store_hooks";
import {mapPathnameToOfferType, OfferType} from "../../../common/app/routing/offer_type";
import {RatioBarChartCard} from "../../../components/charts/chart_cards/BarChartCard";
import {RatioColumnChartCard} from "../../../components/charts/chart_cards/ColumnChartCard";
import {RatioColumnStackedChartCard} from "../../../components/charts/chart_cards/ColumnStackedChartCard";
import {RatioLineColumnChartCard} from "../../../components/charts/chart_cards/LineColumnChartCard";
import {MultilineChartCard, RatioMultilineChartCard} from "../../../components/charts/chart_cards/MultilineChartCard";
import {Grid} from "../../../components/Grid";
import {withDateGuard} from "../../../components/hoc/withDateGuard";
import {withGTM} from "../../../components/hoc/withGTM";
import {ChartsLayout as Layout} from "../../../components/layout/charts_layout/ChartsLayout";
import {
    clearChartsRegionPoc,
    selectChartsSelectedRegion,
    setChartsSelectedRegion
} from "../../../components/map/redux/map_city_poc_slice";
import {IOsmDistrictPolygon} from "../../../components/map/types/osm_district_polygon";
import {
    buildDistrictDrillDownSelection,
    buildMapParentRegionSnapshot,
    restoreParentRegionSelection
} from "../../../components/map/utils/district_map_drill_down";
import {toCityOutlineOsmMapParams} from "../../../components/map/utils/osm_map_region_params";
import {selectAuthStatus} from "../../auth/auth_slice";
import {selectLatestEntry} from "../../ssr/redux/latest_entry_slice";
import {pageviewAlgolyticsHit} from "../../tracking/algolytics/pageview_hits";
import {usePageview} from "../../tracking/use_pageview";
import {ViewType} from "../../tracking/view_type";
import {
    useGetChartsMonthCityStatsQuery,
    useGetChartsMonthCityTypeStatsQuery,
    useGetChartsMonthDistrictStatsQuery
} from "../api/charts_platform_api";
import {DistrictOsmMapCard} from "../components/DistrictOsmMapCard";
import {useChartsRegionId} from "../hooks/use_charts_region_id";
import {useEffectiveCitySlug} from "../hooks/use_effective_city_slug";
import {useSelectedRegionDisplay} from "../hooks/use_selected_region_display";
import {buildDistrictMapValuesBySlug, resolveMapMetric} from "../utils/build_district_map_values_by_slug";
import {mapRtkQueryStatusToRequestStatus} from "../utils/rtq_status_to_request_status";

export const Added = withDateGuard(
    withGTM(() => {
        const {path} = useRouteMatch();
        const citySlug = useEffectiveCitySlug();
        const {isKnownCity, regionLabel} = useSelectedRegionDisplay();
        const regionType = mapCitySlugToRegionType(citySlug);
        usePageview(() => {
            pageviewAlgolyticsHit({citySlug, regionType, viewType: ViewType.ADDED_DETAILS});
        });
        const checkOfferType = mapPathnameToOfferType(path);
        const isFlatOfferType = checkOfferType === OfferType.FLAT;
        const {t, i18n} = useTranslation();
        const {currentDate} = useAppSelector(selectLatestEntry);
        const lastMonth = currentDate ?? "";
        const {isLoggedIn, isAuthorized} = useAppSelector(selectAuthStatus);
        const shouldFetch = isLoggedIn && isAuthorized;
        const dispatch = useAppDispatch();
        const chartsSelectedRegion = useAppSelector(selectChartsSelectedRegion);
        const {regionId, districtChartsRegionId, effectiveCitySlug, hasChartsAccess, isRegionIdReady} =
            useChartsRegionId();
        const isPermitted = isAuthorized && hasChartsAccess;
        const chartsCitySlug = effectiveCitySlug ?? citySlug;
        const isAgglomerationView = getCityOption(chartsCitySlug).useAgglomerationApi;
        const routeMapParams = useMemo(
            () => getCityMapRegionParams(chartsCitySlug, regionId ?? undefined),
            [chartsCitySlug, regionId]
        );
        const baseOsmMapParams = useMemo(
            () =>
                chartsSelectedRegion != null
                    ? {
                          regionsMapSlug: chartsSelectedRegion.regionsMapSlug,
                          regionsMapParentId: chartsSelectedRegion.regionsMapParentId,
                          regionsMapRegionId: chartsSelectedRegion.regionsMapRegionId,
                          regionsMapChildType: chartsSelectedRegion.regionsMapChildType
                      }
                    : routeMapParams,
            [chartsSelectedRegion, routeMapParams]
        );
        const hasDistrictStatsMap = baseOsmMapParams.regionsMapParentId != null;
        const skipChartsFetch = !shouldFetch || !isRegionIdReady || regionId == null;

        const {
            data = [],
            isError,
            isSuccess
        } = useGetChartsMonthCityStatsQuery(
            {
                region_id: regionId!,
                date_start: originDate,
                date_end: lastMonth,
                scenario: "added"
            },
            {skip: skipChartsFetch}
        );
        const {
            data: typeData = [],
            isError: isTypeDataError,
            isSuccess: isTypeDataSuccess
        } = useGetChartsMonthCityTypeStatsQuery(
            {
                region_id: regionId!,
                date_start: originDate,
                date_end: lastMonth,
                offer_type: "Mieszkania",
                scenario: "added"
            },
            {skip: skipChartsFetch}
        );

        const {data: monthDistrictData = []} = useGetChartsMonthDistrictStatsQuery(
            {
                region_id: districtChartsRegionId!,
                date_start: lastMonth,
                date_end: lastMonth,
                scenario: "added",
                ...(isFlatOfferType ? {offer_type: "Mieszkania"} : {})
            },
            {
                skip: skipChartsFetch || districtChartsRegionId == null || (isAgglomerationView && !hasDistrictStatsMap)
            }
        );
        const isCityMultiDistrictView = baseOsmMapParams.regionsMapParentId != null;
        const showDistrictPolygons = isCityMultiDistrictView && monthDistrictData.length > 0;
        const osmMapParams = useMemo(
            () => (showDistrictPolygons ? baseOsmMapParams : toCityOutlineOsmMapParams(baseOsmMapParams)),
            [baseOsmMapParams, showDistrictPolygons]
        );

        const monthStatsData = useMemo(() => (Array.isArray(data) ? data : []), [data]);
        const monthTypeStatsData = useMemo(() => (Array.isArray(typeData) ? typeData : []), [typeData]);
        const dataStatus = mapRtkQueryStatusToRequestStatus(isError, isSuccess);
        const typeDataStatus = mapRtkQueryStatusToRequestStatus(isTypeDataError, isTypeDataSuccess);
        const monthData = monthStatsData.length > 0 ? [monthStatsData[monthStatsData.length - 1]] : [];
        const flatMonthData = monthTypeStatsData.length > 0 ? [monthTypeStatsData[monthTypeStatsData.length - 1]] : [];
        const districtsMapAvailable = Boolean(
            osmMapParams.regionsMapSlug ||
                osmMapParams.regionsMapParentId != null ||
                osmMapParams.regionsMapRegionId != null
        );
        const lastMapStats = isFlatOfferType ? monthTypeStatsData.at(-1) : monthStatsData.at(-1);
        const districtAddedBySlug = useMemo(
            () =>
                buildDistrictMapValuesBySlug({
                    districtsMapAvailable,
                    monthDistrictData,
                    chartsCitySlug,
                    districtMetric: (d) => d.added,
                    cityLevelMetric: resolveMapMetric(lastMapStats?.added),
                    cityLevelStatsRecord: lastMapStats,
                    extraSlugKeys: [osmMapParams.regionsMapSlug, chartsSelectedRegion?.slug]
                }),
            [
                chartsCitySlug,
                chartsSelectedRegion?.slug,
                districtsMapAvailable,
                lastMapStats,
                monthDistrictData,
                osmMapParams.regionsMapSlug
            ]
        );
        const districtDrillDownEnabled = showDistrictPolygons && monthDistrictData.length > 0;
        const districtStatsCitySlug = getCityOption(chartsCitySlug).requestCitySlug;
        const mapParentRegion = chartsSelectedRegion?.parentRegion;
        const handleDistrictPolygonClick = useCallback(
            (district: IOsmDistrictPolygon) => {
                if (
                    districtChartsRegionId == null ||
                    baseOsmMapParams.regionsMapParentId == null ||
                    chartsCitySlug == null
                ) {
                    return;
                }

                const parentLabel = chartsSelectedRegion?.label ?? t(`city.${citySlug}.nominative`);
                const parentSlug = chartsSelectedRegion?.slug ?? baseOsmMapParams.regionsMapSlug ?? "";

                dispatch(
                    setChartsSelectedRegion(
                        buildDistrictDrillDownSelection(
                            district,
                            chartsCitySlug,
                            buildMapParentRegionSnapshot({
                                isRouteCity: chartsSelectedRegion == null,
                                regionId: districtChartsRegionId,
                                slug: parentSlug,
                                label: parentLabel,
                                citySlug: chartsSelectedRegion?.citySlug ?? chartsCitySlug,
                                hasChartsAccess,
                                mapParams: baseOsmMapParams
                            })
                        )
                    )
                );

                window.userengage?.("event.[BD]_district_click", {
                    city: chartsCitySlug,
                    district: district.slug_district ?? district.slug
                });
            },
            [baseOsmMapParams, chartsCitySlug, chartsSelectedRegion, citySlug, dispatch, districtChartsRegionId, t]
        );
        const handleBackToMapParent = useCallback(() => {
            const parent = chartsSelectedRegion?.parentRegion;
            if (!parent) {
                return;
            }

            if (parent.isRouteCity) {
                dispatch(clearChartsRegionPoc());
            } else {
                dispatch(setChartsSelectedRegion(restoreParentRegionSelection(parent)));
            }
        }, [chartsSelectedRegion, dispatch]);

        return (
            <Layout>
                <Grid type={districtsMapAvailable ? "subpage" : "subpageMapless"} isFlatOfferType={isFlatOfferType}>
                    <RatioLineColumnChartCard<IMonthCityStats | IMonthCityTypeStats>
                        area="chart1"
                        chartId="added-chart1"
                        title={<Trans>{t("added.chart1.title")}</Trans>}
                        helper={<Trans>{t("added.chart1.description")}</Trans>}
                        data={isFlatOfferType ? monthTypeStatsData : monthStatsData}
                        dataStatus={isFlatOfferType ? typeDataStatus : dataStatus}
                        columnFields={[
                            "added_studio",
                            "added_2_rooms",
                            "added_3_rooms",
                            "added_4_plus_rooms",
                            ...(isFlatOfferType ? [] : ["added_house"])
                        ]}
                        lineField="added"
                        locked={isRegionIdReady && !isPermitted}
                        empty={!isRegionIdReady}
                    />

                    <RatioBarChartCard<IMonthCityStats | IMonthCityTypeStats>
                        area="chart2"
                        chartId="added-chart2"
                        title={<Trans>{t("added.chart2.title")}</Trans>}
                        helper={<Trans>{t("added.chart2.description")}</Trans>}
                        data={isFlatOfferType ? flatMonthData : monthData}
                        dataStatus={isFlatOfferType ? typeDataStatus : dataStatus}
                        fields={[
                            "added_offers_to_6k",
                            "added_offers_between_6k_and_7k",
                            "added_offers_between_7k_and_8k",
                            "added_offers_between_8k_and_9k",
                            "added_offers_between_9k_and_10k",
                            "added_offers_between_10k_and_12k",
                            "added_offers_between_12k_and_15k",
                            "added_offers_between_15k_and_20k",
                            "added_offers_above_20k"
                        ]}
                        height={225}
                        locked={isRegionIdReady && !isPermitted}
                        empty={!isRegionIdReady}
                    />

                    {!isFlatOfferType && (
                        <MultilineChartCard<IMonthCityStats>
                            area="chart3"
                            chartId="added-chart3"
                            title={<Trans>{t("added.chart3.title")}</Trans>}
                            helper={<Trans>{t("added.chart3.description")}</Trans>}
                            data={monthStatsData}
                            dataStatus={dataStatus}
                            fields={[
                                "added_avg_area_studio",
                                "added_avg_area_2_rooms",
                                "added_avg_area_3_rooms",
                                "added_avg_area_4_plus_rooms"
                            ]}
                            unit=" m<sup>2</sup>"
                            locked={isRegionIdReady && !isPermitted}
                            empty={!isRegionIdReady}
                        />
                    )}

                    <RatioMultilineChartCard<IMonthCityStats | IMonthCityTypeStats>
                        area="chart4"
                        chartId="added-chart4"
                        title={<Trans>{t("added.chart4.title")}</Trans>}
                        helper={<Trans>{t("added.chart4.description")}</Trans>}
                        data={isFlatOfferType ? monthTypeStatsData : monthStatsData}
                        dataStatus={isFlatOfferType ? typeDataStatus : dataStatus}
                        fields={[
                            "added_area_to_30m2",
                            "added_area_between_30_and_50m2",
                            "added_area_between_50_and_70m2",
                            "added_area_between_70_and_100m2",
                            "added_area_between_100_and_120m2",
                            "added_area_above_120m2"
                        ]}
                        locked={isRegionIdReady && !isPermitted}
                        empty={!isRegionIdReady}
                    />

                    <RatioColumnChartCard<IMonthCityStats>
                        area="chart5"
                        chartId="added-chart5"
                        title={<Trans>{t("added.chart5.title")}</Trans>}
                        helper={<Trans>{t("added.chart5.description")}</Trans>}
                        data={monthData}
                        dataStatus={dataStatus}
                        fields={[
                            "added_investment_type_intimate",
                            "added_investment_type_small",
                            "added_investment_type_medium",
                            "added_investment_type_large",
                            "added_investment_type_x_large",
                            ...(isFlatOfferType ? [] : ["added_investment_type_house"])
                        ]}
                        locked={isRegionIdReady && !isPermitted}
                        empty={!isRegionIdReady}
                    />

                    <RatioColumnStackedChartCard
                        area="chart6"
                        chartId="added-chart6"
                        title={<Trans>{t("added.chart6.title")}</Trans>}
                        helper={<Trans>{t("added.chart6.description")}</Trans>}
                        data={monthData}
                        dataStatus={dataStatus}
                        categories={[
                            "investment_type_intimate",
                            "investment_type_small",
                            "investment_type_medium",
                            "investment_type_large",
                            "investment_type_x_large",
                            ...(isFlatOfferType ? [] : ["investment_type_house"])
                        ]}
                        fieldGroups={[
                            [
                                "added_investment_type_intimate_offer_studio",
                                "added_investment_type_small_offer_studio",
                                "added_investment_type_medium_offer_studio",
                                "added_investment_type_large_offer_studio",
                                "added_investment_type_x_large_offer_studio",
                                ...(isFlatOfferType ? [] : ["added_houses_to_100m2"])
                            ],
                            [
                                "added_investment_type_intimate_offer_2_rooms",
                                "added_investment_type_small_offer_2_rooms",
                                "added_investment_type_medium_offer_2_rooms",
                                "added_investment_type_large_offer_2_rooms",
                                "added_investment_type_x_large_offer_2_rooms",
                                ...(isFlatOfferType ? [] : ["added_houses_between_100_and_150m2"])
                            ],
                            [
                                "added_investment_type_intimate_offer_3_rooms",
                                "added_investment_type_small_offer_3_rooms",
                                "added_investment_type_medium_offer_3_rooms",
                                "added_investment_type_large_offer_3_rooms",
                                "added_investment_type_x_large_offer_3_rooms",
                                ...(isFlatOfferType ? [] : ["added_houses_between_150_and_200m2"])
                            ],
                            [
                                "added_investment_type_intimate_offer_4_plus_rooms",
                                "added_investment_type_small_offer_4_plus_rooms",
                                "added_investment_type_medium_offer_4_plus_rooms",
                                "added_investment_type_large_offer_4_plus_rooms",
                                "added_investment_type_x_large_offer_4_plus_rooms",
                                ...(isFlatOfferType ? [] : ["added_houses_between_200_and_250m2"])
                            ],
                            ["", "", "", "", "", ...(isFlatOfferType ? [] : ["added_houses_above_250m2"])]
                        ]}
                        minValue={1}
                        locked={isRegionIdReady && !isPermitted}
                        empty={!isRegionIdReady}
                    />

                    <DistrictOsmMapCard
                        chartId="added-map"
                        title={
                            <Trans>
                                {isKnownCity
                                    ? t("added.map.title", {city: chartsCitySlug})
                                    : t("added.map.title_region", {region: regionLabel})}
                            </Trans>
                        }
                        helper={t("added.map.description")}
                        visible={districtsMapAvailable}
                        sticky
                        citySlug={chartsCitySlug}
                        regionId={regionId}
                        regionsMapSlug={osmMapParams.regionsMapSlug}
                        regionsMapParentId={osmMapParams.regionsMapParentId}
                        regionsMapChildType={osmMapParams.regionsMapChildType}
                        regionsMapRegionId={osmMapParams.regionsMapRegionId}
                        districtsMapAvailable={districtsMapAvailable}
                        districtValuesBySlug={districtAddedBySlug}
                        districtStatsCitySlug={districtStatsCitySlug}
                        metricLabel={t("fields.added")}
                        locale={i18n.language}
                        legendType="quantitative"
                        legendColors={["#FFF2D4", "#FFBD98", "#FF8F6D", "#FF6C40", "#E63600"]}
                        locked={isRegionIdReady && !isPermitted}
                        empty={!isRegionIdReady}
                        cityOutlineMetric={!showDistrictPolygons}
                        districtDrillDownEnabled={districtDrillDownEnabled}
                        onDistrictPolygonClick={handleDistrictPolygonClick}
                        onBackToParentRegion={mapParentRegion ? handleBackToMapParent : undefined}
                        backToParentLabel={mapParentRegion ? `← ${mapParentRegion.label}` : undefined}
                    />
                </Grid>
            </Layout>
        );
    })
);
